import type {V1GuideToWeb} from "@khanacademy/embedded-webapp-schema";
import {updateConversationWithSystemMessage} from "@khan/guide-core";
import {GuideStateApi} from "@khan/guide-state";
import {doNotTranslateYet} from "@khan/i18n";

import {ExtensionPromptVariable} from "./variables.ts";

const SELECTION_DISPLAY_CHARACTER_LIMIT = 300;

// Prefixes for the different annotations we create.
export const NEW_PAGE_PREFIX = "The user has navigated to a new page.";
export const CONTENT_UPDATE_PREFIX =
    "The content of the web page which the user is viewing has changed.";
export const SELECTED_CONTENT_PREFIX =
    "The user has selected content on the page.";
export const NO_SELECTION =
    "The user is no longer selecting content on the page.";

export const newPageAnnotation = (
    url: string,
    title: string,
    content: string,
): string => {
    return `${NEW_PAGE_PREFIX}
            The new page's url is: "${url}" and the title of the page is: "${title}".

            The content of the new web page is: <text>${content}</text>.
        `;
};

export const selectionAnnotation = (selectedContent: string): string => {
    return `${SELECTED_CONTENT_PREFIX}
            The selected content is as follows:
            <text>${selectedContent}</text>
        `;
};

export const createSelectionSnippet = (selectedContent: string): string => {
    return selectedContent.length > SELECTION_DISPLAY_CHARACTER_LIMIT
        ? selectedContent.slice(0, SELECTION_DISPLAY_CHARACTER_LIMIT) + "…"
        : selectedContent;
};

export const contentUpdateAnnotation = (newContent: string): string => {
    return `${CONTENT_UPDATE_PREFIX} The new content is: <text>${newContent}</text>`;
};

export const hasMessageInAnnotations = (
    annotations: string[],
    messagePrefix: string,
): boolean => {
    return annotations.some((msg) => msg.startsWith(messagePrefix));
};

// Remove a message by its prefix, if it exists, from the annotations array in
// place. This is done by prefix matching because the rest of the content is
// flexible and generally we don't care about the specifics of the content,
// only the message type.
export const removeMessageFromAnnotations = (
    annotations: string[],
    messagePrefix: string,
): boolean => {
    const index = annotations.findIndex((msg) => msg.startsWith(messagePrefix));
    if (index > -1) {
        annotations.splice(index, 1);
        return true;
    }

    return false;
};

// Update a message in the annotations array in place if it exists. This is
// done by prefix matching.
export const updateMessageInAnnotations = (
    annotations: string[],
    messagePrefix: string,
    newMessage: string,
): boolean => {
    const index = annotations.findIndex((msg) => msg.startsWith(messagePrefix));
    if (index > -1) {
        annotations[index] = newMessage;
        return true;
    }

    return false;
};

// Event names which the extension can send to the bot.
type extensionGuideEvents = "content_selection";

// Update the annotations array with the new context and return the new list of
// annotations, events which should be sent, and any prompt variables to set.
// The annotations array passed in is not modified.
export const updateAnnotationsWithContext = (
    pageContext: V1GuideToWeb.UpdatePageContext["data"],
    previousContext: V1GuideToWeb.UpdatePageContext["data"] | null,
    existingAnnotations: readonly string[],
): {
    eventsToSend: extensionGuideEvents[];
    promptVariables: Record<string, string>;
    annotations: string[];
} => {
    const eventsToSend: extensionGuideEvents[] = [];
    const promptVariables: Record<string, string> = {};

    const customMessages = existingAnnotations.slice();

    // Update the conversation if the URL changed.
    if (previousContext?.url && pageContext.url !== previousContext?.url) {
        GuideStateApi.set("conversation", (conversation) =>
            updateConversationWithSystemMessage(
                conversation,
                doNotTranslateYet`📃 Khanmigo has followed you to a new page.`,
            ),
        );
    }

    // 1. If the URL changed let the bot know.
    // 2. If we have an existing nav message, just update it
    // always. We may not have navigated in the most recent
    // update, but the bot still doesn't know we did
    // previously.
    // When changing URLs we always update the entire page
    // content for the bot.
    const hasExistingNewPage = hasMessageInAnnotations(
        customMessages,
        NEW_PAGE_PREFIX,
    );
    if (hasExistingNewPage || pageContext.url !== previousContext?.url) {
        const msg = newPageAnnotation(
            pageContext.url,
            pageContext.title ?? "",
            pageContext.pageContent ?? "",
        );

        if (!updateMessageInAnnotations(customMessages, NEW_PAGE_PREFIX, msg)) {
            // Put the navigation msg at the top of the list.
            customMessages.unshift(msg);
        }
    } else if (
        // No URL change, see if the page content itself
        // changed.
        previousContext?.pageContent !== pageContext.pageContent
    ) {
        const msg = contentUpdateAnnotation(pageContext.pageContent ?? "");

        if (
            !updateMessageInAnnotations(
                customMessages,
                CONTENT_UPDATE_PREFIX,
                msg,
            )
        ) {
            customMessages.push(msg);
        }
    }

    let sendContentSelectionEvent = false;

    // The extension doesn't always send selection information.
    // Only process selection context if we're sent something
    // to handle and it has changed since last time.
    const hasSelectionData = pageContext.selectionIncluded;
    const hasSelectedNothing = hasSelectionData && !pageContext.selectedContent;
    if (
        hasSelectionData &&
        (previousContext?.selectedContent !== pageContext.selectedContent ||
            previousContext?.selectionIncluded !==
                pageContext.selectionIncluded)
    ) {
        if (hasSelectionData && !hasSelectedNothing) {
            const msg = selectionAnnotation(pageContext.selectedContent ?? "");

            // Check if we have an existing selection message
            // queued.
            if (
                !updateMessageInAnnotations(
                    customMessages,
                    SELECTED_CONTENT_PREFIX,
                    msg,
                )
            ) {
                customMessages.push(msg);
            }

            // Remove the "no selection" message from the queue
            // if it exists because we have a selection now.
            removeMessageFromAnnotations(customMessages, NO_SELECTION);

            sendContentSelectionEvent = true;
        } else if (
            // User has not selected anything. Ensure the bot
            // knows that.
            hasSelectedNothing
        ) {
            removeMessageFromAnnotations(
                customMessages,
                SELECTED_CONTENT_PREFIX,
            );
            removeMessageFromAnnotations(customMessages, NO_SELECTION);

            customMessages.push(NO_SELECTION);
        }
    }

    // Wait until the end to send the content selection event
    // so that all the custom messages are queued up first.
    if (sendContentSelectionEvent) {
        promptVariables[ExtensionPromptVariable.ExtensionSelection] =
            pageContext.selectedContent || "";

        eventsToSend.push("content_selection");
    }

    return {
        eventsToSend,
        promptVariables,
        annotations: customMessages,
    };
};
